import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Briefcase,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gift,
  Trophy,
  Award,
  Package,
  Building,
  Target,
  Gem,
  Palette,
  User,
  Ruler,
  Tag,
  Hash
} from 'lucide-react';
import { toast } from 'sonner';

// Product type options for Business
const PRODUCT_TYPES = [
  { value: 'gadget', label: 'Gadget Aziendali', icon: Gift },
  { value: 'premi', label: 'Premi e Riconoscimenti', icon: Award },
  { value: 'targhe', label: 'Targhe Personalizzate', icon: Tag },
  { value: 'trofei', label: 'Trofei e Coppe', icon: Trophy },
  { value: 'packaging', label: 'Packaging e Confezioni', icon: Package },
  { value: 'allestimenti', label: 'Allestimenti ed Espositori', icon: Building },
  { value: 'regali_corporate', label: 'Regali Corporate', icon: Gem },
  { value: 'welcome_kit', label: 'Welcome Kit Dipendenti', icon: Users },
  { value: 'merchandising', label: 'Merchandising Aziendale', icon: Sparkles },
  { value: 'altro', label: 'Altro', icon: Briefcase },
];

// Material/Type options
const MATERIAL_OPTIONS = [
  { value: 'metallo', label: 'Metallo (ottone, alluminio, acciaio)', icon: Shield },
  { value: 'legno', label: 'Legno pregiato (noce, rovere, ciliegio)', icon: Palette },
  { value: 'cristallo', label: 'Cristallo / Vetro', icon: Gem },
  { value: 'pelle', label: 'Pelle e Cuoio', icon: Star },
  { value: 'tessuto', label: 'Tessuto / Abbigliamento', icon: Tag },
  { value: 'carta', label: 'Carta / Cartone (eco-friendly)', icon: Package },
  { value: 'plastica', label: 'Plastica / Plexiglass', icon: Zap },
  { value: 'misto', label: 'Materiali misti', icon: Palette },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: MessageSquare },
];

// Personalization options
const PERSONALIZATION_OPTIONS = [
  { value: 'logo', label: 'Logo aziendale', icon: Building },
  { value: 'incisione', label: 'Incisione personalizzata', icon: Palette },
  { value: 'stampa', label: 'Stampa a colori', icon: Gem },
  { value: 'ricamo', label: 'Ricamo', icon: Star },
  { value: 'serigrafia', label: 'Serigrafia', icon: Tag },
  { value: 'nessuna', label: 'Nessuna personalizzazione', icon: Package },
  { value: 'altro', label: 'Altro (specificare)', icon: Sparkles },
];

// Quantity options
const QUANTITY_OPTIONS = [
  { value: '1-10', label: '1 - 10 pezzi', description: 'Campioni / Prova' },
  { value: '10-50', label: '10 - 50 pezzi', description: 'Piccola serie' },
  { value: '50-100', label: '50 - 100 pezzi', description: 'Media serie' },
  { value: '100-250', label: '100 - 250 pezzi', description: 'Grande serie' },
  { value: '250-500', label: '250 - 500 pezzi', description: 'Fornitura aziendale' },
  { value: '500+', label: '500+ pezzi', description: 'Volume elevato' },
];

// Purpose options
const PURPOSE_OPTIONS = [
  { value: 'evento', label: 'Evento / Fiera / Conferenza', icon: Users },
  { value: 'regalo_clienti', label: 'Regalo per clienti', icon: Gift },
  { value: 'regalo_dipendenti', label: 'Regalo per dipendenti', icon: User },
  { value: 'premiazione', label: 'Premiazione / Riconoscimento', icon: Award },
  { value: 'merchandising', label: 'Merchandising / Promo', icon: Tag },
  { value: 'onboarding', label: 'Onboarding nuovi assunti', icon: Users },
  { value: 'natale', label: 'Regali di Natale aziendali', icon: Gift },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '100-500', label: '€100 - €500', description: 'Budget contenuto' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Budget medio' },
  { value: '1000-2500', label: '€1.000 - €2.500', description: 'Budget importante' },
  { value: '2500-5000', label: '€2.500 - €5.000', description: 'Progetto aziendale' },
  { value: '5000-10000', label: '€5.000 - €10.000', description: 'Fornitura completa' },
  { value: '10000+', label: '€10.000+', description: 'Grande progetto' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '1month', label: '1 Mese', description: 'Standard' },
  { value: '2months', label: '2 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example business projects for showcase
const BUSINESS_EXAMPLES = [
  {
    idea: "Trofei personalizzati per la convention annuale di vendita",
    result: "20 trofei in cristallo con incisione laser 3D del logo aziendale, basamento in legno noce con targhetta nominativa",
    image: "https://images.unsplash.com/photo-1514820720301-4c4790309f46?w=400&h=300&fit=crop",
    category: "Trofei"
  },
  {
    idea: "Welcome kit per nuovi dipendenti startup tech",
    result: "Kit premium con borsa in cotone brandizzata, agenda in pelle, penna stilografica, power bank e tazza termica personalizzata",
    image: "https://images.unsplash.com/photo-1558365916-848463c5d803?w=400&h=300&fit=crop",
    category: "Welcome Kit"
  },
  {
    idea: "Packaging di lusso per regali natalizi ai top client",
    result: "50 cofanetti in legno con cerniera magnetica, interno in velluto, prodotti gourmet italiani e biglietto calligrafico personalizzato",
    image: "https://images.unsplash.com/photo-1694481901573-a970f982ac5e?w=400&h=300&fit=crop",
    category: "Packaging"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Abbiamo commissionato 150 targhe premio per la nostra rete vendita. Qualità eccellente, personalizzazione impeccabile e consegna puntuale. Consiglio vivamente!",
    author: "Marco R.",
    role: "Direttore Commerciale",
    company: "Azienda Farmaceutica"
  },
  {
    text: "I welcome kit per i nuovi assunti hanno fatto un'impressione incredibile. Finalmente gadget di qualità e non i soliti oggetti anonimi. Torneremo sicuramente!",
    author: "Giulia T.",
    role: "HR Manager",
    company: "Startup Fintech"
  },
  {
    text: "Stand fieristico progettato su misura per il Salone del Mobile. Design unico che ci ha fatto distinguere dalla concorrenza. Collaborazione perfetta!",
    author: "Alessandro B.",
    role: "Marketing Director",
    company: "Azienda Arredamento"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quali quantitativi minimi posso ordinare?",
    answer: "Non ci sono quantitativi minimi obbligatori. Puoi richiedere anche un singolo pezzo per progetti speciali o campioni. Per ordini in serie, i nostri artigiani possono proporre sconti quantità."
  },
  {
    question: "Posso vedere un campione prima dell'ordine completo?",
    answer: "Certamente! Molti artigiani offrono la possibilità di realizzare un campione/prototipo prima della produzione in serie. Specifica questa esigenza nella richiesta."
  },
  {
    question: "Come funziona la personalizzazione con il logo aziendale?",
    answer: "Puoi caricare il tuo logo in formato vettoriale (AI, EPS, SVG) o ad alta risoluzione (PNG, PDF). L'artigiano ti proporrà le migliori tecniche di personalizzazione in base al materiale scelto."
  },
  {
    question: "Emettete fattura per acquisti aziendali?",
    answer: "Sì, tutti gli acquisti su Rarifex possono essere fatturati. Inserisci i dati aziendali (P.IVA, Ragione Sociale) in fase di checkout o comunicali direttamente all'artigiano."
  },
  {
    question: "Quali sono i tempi di consegna per ordini in serie?",
    answer: "I tempi variano in base alla complessità e quantità: ordini piccoli (1-50 pezzi) richiedono 1-3 settimane, ordini grandi (100+ pezzi) possono richiedere 3-6 settimane. Specifica le tue esigenze temporali nella richiesta."
  }
];

export default function BusinessRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    company_name: '',
    product_type: '',
    material: '',
    personalization: '',
    quantity: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Articoli Business e Corporate | Gadget e Premi Aziendali | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 file');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
        toast.error('Formato non supportato. Usa immagini o PDF');
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File troppo grande (max 10MB)');
        continue;
      }
      
      try {
        const fileName = `business-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi le tue esigenze aziendali');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_business_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const productLabel = PRODUCT_TYPES.find(t => t.value === formData.product_type)?.label || '';
      const materialLabel = MATERIAL_OPTIONS.find(t => t.value === formData.material)?.label || '';
      const personalizationLabel = PERSONALIZATION_OPTIONS.find(t => t.value === formData.personalization)?.label || '';
      const quantityLabel = QUANTITY_OPTIONS.find(q => q.value === formData.quantity)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (formData.company_name) details.push(`🏢  Azienda: ${formData.company_name}`);
      if (productLabel) details.push(`💼  Tipo: ${productLabel}`);
      if (materialLabel) details.push(`🧱  Materiale: ${materialLabel}`);
      if (personalizationLabel) details.push(`✨  Personalizzazione: ${personalizationLabel}`);
      if (quantityLabel) details.push(`📦  Quantità: ${quantityLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA BUSINESS\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${productLabel} - Business`.trim() || 'Richiesta articoli Business',
        description: fullDescription,
        category: 'Business',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('business-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Articoli Business e Corporate | Gadget e Premi Aziendali Made in Italy | Rarifex</title>
        <meta name="description" content="Articoli business e corporate Made in Italy: gadget aziendali, premi, targhe, trofei, packaging, regali corporate. Artigiani italiani per il tuo brand. Preventivi gratuiti." />
        <meta name="keywords" content="gadget aziendali personalizzati, premi aziendali, targhe personalizzate, trofei aziendali, packaging corporate, regali aziendali, welcome kit, merchandising italiano" />
        <link rel="canonical" href="https://www.rarifex.com/business-su-misura" />
        <meta property="og:title" content="Articoli Business e Corporate | Gadget Aziendali Made in Italy | Rarifex" />
        <meta property="og:description" content="Gadget, premi, targhe e regali aziendali Made in Italy. Artigiani italiani per valorizzare il tuo brand. Preventivi gratuiti." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/business-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50/50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Briefcase className="w-4 h-4" />
                    Articoli Business Made in Italy
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il tuo brand{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-blue-700">
                      merita l&apos;eccellenza
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani creano gadget, premi e regali aziendali di qualità superiore.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Gadget personalizzati, trofei, targhe, packaging di lusso e welcome kit:
                    <br />
                    <strong className="text-slate-700">valorizza il tuo brand con il Made in Italy.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-slate-500" />
                      <span>Preventivi in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="business-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Company Name */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Nome Azienda
                        </Label>
                        <Input
                          placeholder="Es: Acme Srl"
                          value={formData.company_name}
                          onChange={(e) => handleInputChange('company_name', e.target.value)}
                          className="mt-1.5"
                          data-testid="business-company-input"
                        />
                      </div>
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del progetto
                        </Label>
                        <Input
                          placeholder="Es: Gadget per convention annuale"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="business-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi le tue esigenze *
                        </Label>
                        <Textarea
                          placeholder="Racconta il progetto... Cosa ti serve? Per quale occasione? Hai specifiche tecniche o di branding?"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[100px]"
                          required
                          data-testid="business-description-textarea"
                        />
                      </div>
                      
                      {/* Product Type & Material */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo articolo</Label>
                          <select
                            value={formData.product_type}
                            onChange={(e) => handleInputChange('product_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {PRODUCT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Materiale</Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIAL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Personalization & Quantity */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Personalizzazione</Label>
                          <select
                            value={formData.personalization}
                            onChange={(e) => handleInputChange('personalization', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-personalization-select"
                          >
                            <option value="">Seleziona...</option>
                            {PERSONALIZATION_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Quantità</Label>
                          <select
                            value={formData.quantity}
                            onChange={(e) => handleInputChange('quantity', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-quantity-select"
                          >
                            <option value="">Seleziona...</option>
                            {QUANTITY_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Purpose & Budget */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="business-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                          data-testid="business-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Logo, immagini o brief (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-slate-400 hover:bg-slate-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica file (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*,.pdf,.ai,.eps,.svg"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                          <p className="text-xs text-slate-400 mt-1">
                            Accettiamo immagini, PDF e file vettoriali (AI, EPS, SVG)
                          </p>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                {url.endsWith('.pdf') || url.includes('.ai') || url.includes('.eps') || url.includes('.svg') ? (
                                  <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                                    <Package className="w-6 h-6 text-slate-600" />
                                  </div>
                                ) : (
                                  <img
                                    src={url}
                                    alt={`Allegato ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded-lg"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-slate-700 to-blue-700 hover:from-slate-800 hover:to-blue-800 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="business-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da artigiani verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai articoli business di qualità artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci le tue esigenze aziendali' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da artigiani specializzati' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il preventivo migliore' },
                { icon: Gift, title: 'Ricevi', desc: 'Articoli di qualità Made in Italy' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-slate-100 to-blue-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-slate-700" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHAT WE OFFER */}
        <section className="py-16 bg-gradient-to-b from-white to-slate-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Puoi Richiedere
              </h2>
              <p className="text-lg text-slate-600">
                I nostri artigiani realizzano ogni tipo di articolo business
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { icon: Gift, label: 'Gadget Aziendali', color: 'bg-blue-100 text-blue-600' },
                { icon: Award, label: 'Premi e Medaglie', color: 'bg-amber-100 text-amber-600' },
                { icon: Tag, label: 'Targhe', color: 'bg-slate-100 text-slate-600' },
                { icon: Trophy, label: 'Trofei e Coppe', color: 'bg-yellow-100 text-yellow-600' },
                { icon: Package, label: 'Packaging', color: 'bg-orange-100 text-orange-600' },
                { icon: Building, label: 'Allestimenti', color: 'bg-purple-100 text-purple-600' },
                { icon: Gem, label: 'Regali Corporate', color: 'bg-rose-100 text-rose-600' },
                { icon: Users, label: 'Welcome Kit', color: 'bg-green-100 text-green-600' },
              ].map((item, index) => (
                <div key={index} className={`${item.color} rounded-xl p-4 text-center`}>
                  <item.icon className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium text-sm">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Progetti Realizzati
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta al prodotto finito
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {BUSINESS_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-slate-100 text-slate-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-gradient-to-b from-slate-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono le Aziende
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">&ldquo;{testimonial.text}&rdquo;</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.role}</p>
                    <p className="text-slate-400 text-xs">{testimonial.company}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-slate-700 to-blue-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Valorizza il tuo brand con l&apos;artigianato italiano
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti per i tuoi articoli business
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-slate-700 hover:bg-slate-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="business-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dagli artigiani, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-slate-700 hover:bg-slate-800"
                  onClick={() => navigate('/login?redirect=/business-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
